import type { ReactNode } from 'react';
import { colorVarKey } from './chart-series';
import { SeriesMark } from './series-mark';
import type { ChartSeries } from './types';

/** recharts 가 주입하는 payload 항목(필요한 필드만). */
interface PayloadItem {
  dataKey?: string | number;
  value?: number | string | null;
}

export interface CartesianTooltipProps {
  /** key → 시리즈 메타(라벨·포맷). `CartesianChart` 가 주입. */
  seriesByKey: Map<string, ChartSeries>;
  /** x 도메인 라벨 포맷. */
  labelFormatter?: (label: string) => string;
  /**
   * 시리즈별 예외 주석(선택) — `(dataKey, 도메인)` 로 짧은 문자열을 반환하면 값 뒤에 흐리게 덧붙인다. 예: x가
   * 대표시각으로 묶였을 때 실제 수신 시각이 다르면 "(07:11 수신)" 표기. 미반환(undefined)이면 주석 없음.
   */
  annotate?: (dataKey: string, domain: string) => string | undefined;
  /** 값이 0 인 시리즈를 툴팁에서 숨김(선택) — 예: MPPT 출력의 미가동(0kW) 채널 노이즈 제거. */
  hideZero?: boolean;
  /**
   * 값이 0 인 시리즈의 값 대신 표기할 문구(선택, 예: '(미출력)') — `hideZero` 와 달리 숨기지 않고 destructive 색으로
   * 표기해 미가동 채널을 명시한다(예: 스트링 출력의 0 채널). 미지정 시 0 도 숫자로 표시.
   */
  zeroLabel?: string;
  /**
   * 표시 대상이 **전부 0**일 때 개별 나열 대신 보일 한 줄 destructive 문구(선택, 예: '전체 미출력'). `zeroLabel`
   * 과 함께 스트링 출력에서 사용. `hideZero` 와는 무관(hideZero 는 0 을 아예 제외).
   */
  allZeroLabel?: string;
  /** 표시할 시리즈가 하나도 없을 때(전부 결측, 또는 hideZero 로 전부 제외) 대신 보일 한 줄 문구(선택, 예: '출력 없음'). */
  emptyLabel?: string;
  /**
   * 행 배치 열 수(선택, 기본 1=세로 스택). >1 이면 행을 `columns`열 그리드로 흘려 세로 길이를 줄인다(예: 스트링
   * 출력 12~24 채널을 3열로). Tailwind arbitrary 회피 위해 `gridTemplateColumns` 는 inline style(viz 는 admin
   * @source 밖이라 임의 클래스 미생성).
   */
  columns?: number;
  // ↓ recharts 가 런타임에 주입(content cloneElement)
  active?: boolean;
  payload?: PayloadItem[];
  label?: string | number;
}

/**
 * 기본 툴팁 — 모델 `format` 으로 단위까지 표시(앱 viz 패리티).
 * heritage 토큰 사용(`bg-background`/`border-border`/`text-muted-foreground`).
 */
export function CartesianTooltip({
  seriesByKey,
  labelFormatter,
  annotate,
  hideZero,
  zeroLabel,
  allZeroLabel,
  emptyLabel,
  columns,
  active,
  payload,
  label,
}: CartesianTooltipProps) {
  if (!active || !payload?.length) return null;

  const domain = String(label ?? '');

  // 결측(null)만 제외한 실 데이터 — all-zero 판정은 **hideZero 이전** 원본 기준(전부 0 이면 MPPT·스트링 공통으로
  // 한 줄 처리). 0 개별 취급(숨김/표기)은 일부만 0 일 때만 갈린다.
  const items = payload.flatMap((item) => {
    const key = String(item.dataKey ?? '');
    const meta = seriesByKey.get(key);
    if (!meta || item.value == null) return [];
    const num = typeof item.value === 'number' ? item.value : Number(item.value);
    return [{ key, meta, num }];
  });

  const shell = (children: ReactNode): ReactNode => (
    <div className="grid min-w-32 items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
      <div className="font-medium text-foreground">
        {labelFormatter ? labelFormatter(domain) : domain}
      </div>
      {children}
    </div>
  );
  // 표시할 게 없을 때(전부 결측 / hideZero 로 전부 제외) — emptyLabel 한 줄 또는 숨김(빈 박스 방지).
  const emptyReturn = emptyLabel
    ? shell(<div className="text-muted-foreground">{emptyLabel}</div>)
    : null;

  // 전부 결측.
  if (items.length === 0) return emptyReturn;

  // 전부 0(미출력) → 개별 나열 대신 destructive 한 줄('전체 미출력'). MPPT·스트링 공통(hideZero 무관).
  if (allZeroLabel && items.every((i) => i.num === 0)) {
    return shell(<div className="text-destructive">{allZeroLabel}</div>);
  }

  // 일부만 0 — MPPT(hideZero)는 0 채널 제외, 스트링(zeroLabel)은 0 을 '(미출력)' 로 표기(아래 rows).
  const visible = hideZero ? items.filter((i) => i.num !== 0) : items;
  if (visible.length === 0) return emptyReturn;

  const rows = visible.map(({ key, meta, num }) => {
    const formatted = meta.format?.(num);
    const note = annotate?.(key, domain);
    return (
      <div key={key} className="flex items-center gap-1.5">
        {/* 마크는 범례와 동일 규칙(`SeriesMark`) — 막대=네모 · 라인=선+점. 색은 범례처럼 시리즈 색을 직접 쓰지
            않고 ChartContainer 가 주입한 `--color-{key}`(recharts payload 기준). */}
        <SeriesMark
          type={meta.type}
          color={`var(--color-${colorVarKey(key)})`}
          showDots={meta.showDots}
          size="sm"
        />
        <span className="text-muted-foreground">{meta.label}</span>
        <span className="ml-auto tabular-nums">
          {zeroLabel && num === 0 ? (
            <span className="text-destructive">{zeroLabel}</span>
          ) : (
            <span className="text-foreground">
              {formatted ? `${formatted.value}${formatted.unit}` : num.toLocaleString()}
            </span>
          )}
          {note ? <span className="ml-1 text-[10px] text-muted-foreground">{note}</span> : null}
        </span>
      </div>
    );
  });

  return shell(
    <div
      className="grid gap-1.5"
      style={
        columns && columns > 1
          ? { gridTemplateColumns: `repeat(${columns}, max-content)`, columnGap: '0.75rem' }
          : undefined
      }
    >
      {rows}
    </div>,
  );
}
